import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { duplicateOfferLetterTemplate } from '../api';

/**
 * Duplicate Template dialog.
 *
 * One-click full clone of a template (incl. pdf_terms_list, clauses,
 * salary_structure, terms_and_conditions, AND all annexure DB rows).
 * Used from the Templates page card "Duplicate" button.
 *
 * Why this exists:
 *   - Preview & production use SEPARATE MongoDB clusters. A clone done in
 *     preview is NOT carried over by deployment (deployment ships code,
 *     not data). HR needs the ability to clone templates on whichever
 *     environment they're sitting on, without engineering involvement.
 *   - The existing "Create Template" path requires HR to manually re-enter
 *     14-20 PDF clauses + re-upload annexures — error-prone and slow.
 */
const DuplicateTemplateDialog = ({ template, open, onOpenChange, onDuplicated }) => {
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill the new-name field whenever the dialog opens with a fresh
  // template. Default suffix " (Copy)" lets HR rename or just click Submit.
  useEffect(() => {
    if (open && template) {
      setNewName(`${template.name || ''} (Copy)`);
    }
  }, [open, template]);

  const handleSubmit = async () => {
    const trimmed = (newName || '').trim();
    if (!trimmed) {
      toast.error('Please enter a name for the new template');
      return;
    }
    if (!template?.id) return;
    setSubmitting(true);
    try {
      const res = await duplicateOfferLetterTemplate(template.id, { new_name: trimmed });
      const cloneInfo = res.data?._clone_info || {};
      toast.success(
        `Template duplicated: "${res.data.name}" (${cloneInfo.annexures_cloned || 0} annexure(s) cloned)`
      );
      onDuplicated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to duplicate template';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !submitting) handleSubmit();
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="duplicate-template-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy size={18} className="text-indigo-600" />
            Duplicate Template
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Creates a full copy of <strong>{template.name}</strong> including all PDF terms,
            clauses, salary structure, terms &amp; conditions, and annexure attachments
            (the underlying PDF files are shared, not duplicated).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="new-template-name" className="text-sm font-medium">
              New template name
            </Label>
            <Input
              id="new-template-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter a unique name…"
              disabled={submitting}
              autoFocus
              data-testid="duplicate-template-name-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Must be unique (case-insensitive). Existing template names will be rejected.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="duplicate-template-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !newName?.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="duplicate-template-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" />
                Duplicating…
              </>
            ) : (
              <>
                <Copy size={14} className="mr-1" />
                Duplicate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateTemplateDialog;
